import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ImagePlus, Trash2, PencilLine, Save, XCircle, Loader, Send, Star, Search, Eye, Copy, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../context/AuthContext';
import { useContent, PRODUCT_CATEGORIES, GALLERY_CATEGORIES, normalizeProductCategory } from '../context/ContentContext';
import apiService from '../services/api.service';
import testimonialService from '../services/testimonial.service';
import { toast } from 'sonner';

const Admin = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAuth();
  const { products, galleryItems } = useContent();

  // Tab state
  const [activeTab, setActiveTab] = useState('products');
  
  // Search/filter state
  const [productSearch, setProductSearch] = useState('');
  const [gallerySearch, setGallerySearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Backend data
  const [backendProducts, setBackendProducts] = useState([]);
  const [backendGalleryItems, setBackendGalleryItems] = useState([]);
  const [isLoadingBackend, setIsLoadingBackend] = useState(true);

  const [productForm, setProductForm] = useState({
    name: '',
    category: 'energy-clearing',
    price: '₹',
    description: '',
    image: '',
    inStock: true,
    rating: 4.8,
    reviews: 0
  });
  const [productImagePreview, setProductImagePreview] = useState('');
  const [isSubmittingProduct, setIsSubmittingProduct] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);

  const [galleryForm, setGalleryForm] = useState({
    title: '',
    category: 'meditation',
    description: '',
    date: '',
    src: ''
  });
  const [galleryImagePreview, setGalleryImagePreview] = useState('');
  const [editingGalleryId, setEditingGalleryId] = useState(null);
  const [isSubmittingGallery, setIsSubmittingGallery] = useState(false);

  // Testimonial state
  const [testimonials, setTestimonials] = useState([]);
  const [pendingTestimonials, setPendingTestimonials] = useState([]);
  const [isLoadingTestimonials, setIsLoadingTestimonials] = useState(false);
  const [testimonialForm, setTestimonialForm] = useState({
    customerName: '',
    customerEmail: '',
    text: '',
    rating: 5
  });
  const [editingTestimonialId, setEditingTestimonialId] = useState(null);
  const [isSubmittingTestimonial, setIsSubmittingTestimonial] = useState(false);

  // Load backend data
  useEffect(() => {
    const loadBackendData = async () => {
      setIsLoadingBackend(true);
      try {
        const [productsRes, galleryRes, testimonialsRes, pendingRes] = await Promise.all([
          apiService.adminProducts.getAll(),
          apiService.gallery.getAll(),
          testimonialService.getAll().catch(() => ({ data: [] })),
          testimonialService.getPending().catch(() => ({ data: [] }))
        ]);
        
        console.log('[Admin] Products response:', productsRes);
        console.log('[Admin] Gallery response:', galleryRes);
        console.log('[Admin] Testimonials response:', testimonialsRes);
        console.log('[Admin] Pending testimonials response:', pendingRes);
        
        // Handle different response structures (data or data.data)
        const productsData = productsRes?.data?.data || productsRes?.data;
        const galleryData = galleryRes?.data?.data || galleryRes?.data;
        const testimonialsData = testimonialsRes?.data?.data || testimonialsRes?.data;
        const pendingData = pendingRes?.data?.data || pendingRes?.data;
        
        if (productsData) {
          const products = Array.isArray(productsData) ? productsData : [];
          console.log('[Admin] Setting products:', products.length, 'items');
          setBackendProducts(products);
        }
        if (galleryData) {
          const gallery = Array.isArray(galleryData) ? galleryData : [];
          console.log('[Admin] Setting gallery:', gallery.length, 'items');
          setBackendGalleryItems(gallery);
        }
        if (testimonialsData) {
          const testimonials = Array.isArray(testimonialsData) ? testimonialsData : [];
          console.log('[Admin] Setting testimonials:', testimonials.length, 'items');
          setTestimonials(testimonials);
        }
        if (pendingData) {
          const pending = Array.isArray(pendingData) ? pendingData : [];
          console.log('[Admin] Setting pending testimonials:', pending.length, 'items');
          setPendingTestimonials(pending);
        }
      } catch (error) {
        console.error('[Admin] Failed to load backend data:', error);
        toast.error('Failed to load data from server');
      } finally {
        setIsLoadingBackend(false);
      }
    };

    if (isAdmin) {
      loadBackendData();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/');
    }
  }, [authLoading, isAdmin, navigate]);

  const productCategories = useMemo(() => PRODUCT_CATEGORIES.filter((item) => item.value !== 'all'), []);
  const galleryCategories = useMemo(() => GALLERY_CATEGORIES.filter((item) => item.value !== 'all'), []);

  // Filtered products
  const filteredProducts = useMemo(() => {
    return backendProducts.filter(product => {
      const matchesSearch = product.name?.toLowerCase().includes(productSearch.toLowerCase()) ||
                           product.category?.toLowerCase().includes(productSearch.toLowerCase());
      const matchesCategory = !selectedCategory || normalizeProductCategory(product.category) === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [backendProducts, productSearch, selectedCategory]);

  // Filtered gallery items
  const filteredGalleryItems = useMemo(() => {
    return backendGalleryItems.filter(item => {
      const matchesSearch = item.title?.toLowerCase().includes(gallerySearch.toLowerCase()) ||
                           item.category?.toLowerCase().includes(gallerySearch.toLowerCase());
      return matchesSearch;
    });
  }, [backendGalleryItems, gallerySearch]);

  const handleProductInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleProductImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result?.toString() || '';
      setProductForm((prev) => ({ ...prev, image: result }));
      setProductImagePreview(result);
    };
    reader.readAsDataURL(file);
  };

  const handleProductImageUrl = (e) => {
    const value = e.target.value;
    setProductForm((prev) => ({ ...prev, image: value }));
    setProductImagePreview(value);
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      category: 'energy-clearing',
      price: '₹',
      description: '',
      image: '',
      inStock: true,
      rating: 4.8,
      reviews: 0
    });
    setProductImagePreview('');
    setEditingProductId(null);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();

    // Debug: Check all localStorage contents
    console.log('[Admin] All localStorage keys:', Object.keys(localStorage));
    console.log('[Admin] All localStorage contents:', {
      token: localStorage.getItem('token'),
      user: localStorage.getItem('user'),
      allKeys: Object.keys(localStorage).map(key => ({
        key,
        valuePreview: localStorage.getItem(key)?.substring(0, 50)
      }))
    });

    // Verify authentication token exists
    const token = localStorage.getItem('token');
    console.log('[Admin] Product save - Token check:', { 
      hasToken: !!token, 
      tokenPreview: token ? token.substring(0, 20) + '...' : 'NONE',
      tokenLength: token ? token.length : 0
    });
    
    if (!token) {
      console.error('[Admin] No token found in localStorage');
      toast.error('Authentication required. Please login first.');
      navigate('/login');
      return;
    }

    if (!productForm.name || !productForm.category || !productForm.price || !productForm.image) {
      toast.error('Please fill in all required product fields');
      return;
    }

    const normalizedProductForm = {
      ...productForm,
      category: normalizeProductCategory(productForm.category)
    };

    console.log('[Admin] Creating product with data:', normalizedProductForm);
    setIsSubmittingProduct(true);
    try {
      if (editingProductId) {
        console.log('[Admin] Updating product:', editingProductId);
        const response = await apiService.adminProducts.update(editingProductId, normalizedProductForm);
        if (response?.data) {
          setBackendProducts((prev) =>
            prev.map((item) => (item.id === editingProductId ? response.data : item))
          );
          toast.success('Product updated successfully');
          resetProductForm();
        }
      } else {
        console.log('[Admin] Creating new product');
        const response = await apiService.adminProducts.create(normalizedProductForm);
        console.log('[Admin] Product creation response:', response);
        if (response?.data) {
          setBackendProducts((prev) => [...prev, response.data]);
          toast.success('Product created successfully');
          resetProductForm();
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save product');
    } finally {
      setIsSubmittingProduct(false);
    }
  };

  const handleEditProduct = (product) => {
    setProductForm({
      name: product.name || '',
      category: normalizeProductCategory(product.category || 'energy-clearing'),
      price: product.price || '₹',
      description: product.description || '',
      image: product.image || '',
      inStock: product.inStock ?? true,
      rating: product.rating ?? 4.8,
      reviews: product.reviews ?? 0
    });
    setProductImagePreview(product.image || '');
    setEditingProductId(product.id);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      await apiService.adminProducts.delete(id);
      setBackendProducts((prev) => prev.filter((item) => item.id !== id));
      toast.success('Product deleted successfully');
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleGalleryInputChange = (e) => {
    const { name, value } = e.target;
    setGalleryForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGalleryImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result?.toString() || '';
      setGalleryForm((prev) => ({ ...prev, src: result }));
      setGalleryImagePreview(result);
    };
    reader.readAsDataURL(file);
  };

  const handleGalleryImageUrl = (e) => {
    const value = e.target.value;
    setGalleryForm((prev) => ({ ...prev, src: value }));
    setGalleryImagePreview(value);
  };

  const resetGalleryForm = () => {
    setGalleryForm({
      title: '',
      category: 'meditation',
      description: '',
      date: '',
      src: ''
    });
    setGalleryImagePreview('');
    setEditingGalleryId(null);
  };

  const handleSaveGallery = async (e) => {
    e.preventDefault();

    // Verify authentication token exists
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication required. Please login first.');
      navigate('/login');
      return;
    }

    if (!galleryForm.title || !galleryForm.category || !galleryForm.src) {
      toast.error('Please fill in all required gallery fields');
      return;
    }

    setIsSubmittingGallery(true);
    try {
      if (editingGalleryId) {
        const response = await apiService.gallery.update(editingGalleryId, galleryForm);
        setBackendGalleryItems((prev) =>
          prev.map((item) => (item.id === editingGalleryId ? response.data : item))
        );
        toast.success('Gallery item updated');
      } else {
        const response = await apiService.gallery.create(galleryForm);
        setBackendGalleryItems((prev) => [...prev, response.data]);
        toast.success('Gallery item added');
      }
      resetGalleryForm();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save gallery item');
    } finally {
      setIsSubmittingGallery(false);
    }
  };

  const handleEditGallery = (item) => {
    setGalleryForm({
      title: item.title || '',
      category: item.category || 'meditation',
      description: item.description || '',
      date: item.date || '',
      src: item.src || ''
    });
    setGalleryImagePreview(item.src || '');
    setEditingGalleryId(item.id);
  };

  const handleDeleteGallery = async (id) => {
    if (!window.confirm('Are you sure you want to delete this gallery item?')) return;

    try {
      await apiService.gallery.delete(id);
      setBackendGalleryItems((prev) => prev.filter((item) => item.id !== id));
      toast.success('Gallery item deleted successfully');
    } catch (error) {
      toast.error('Failed to delete gallery item');
    }
  };

  // Testimonial Handlers
  const handleSubmitTestimonial = async () => {
    // Verify authentication token exists
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Authentication required. Please login first.');
      navigate('/login');
      return;
    }

    if (!testimonialForm.customerEmail || !testimonialForm.customerName || !testimonialForm.text) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmittingTestimonial(true);
    try {
      if (editingTestimonialId) {
        // Update testimonial
        const response = await testimonialService.update(editingTestimonialId, {
          text: testimonialForm.text,
          rating: testimonialForm.rating
        });
        setTestimonials((prev) =>
          prev.map((item) => (item.id === editingTestimonialId ? response.data : item))
        );
        toast.success('Testimonial updated');
      } else {
        // Create new testimonial on behalf of customer
        const response = await testimonialService.createOnBehalf(testimonialForm);
        setTestimonials((prev) => [...prev, response.data]);
        setPendingTestimonials((prev) => [...prev, response.data]);
        
        // Send approval email
        await testimonialService.sendApprovalEmail(response.data.id);
        toast.success('Testimonial created and approval email sent to customer');
      }
      resetTestimonialForm();
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save testimonial');
    } finally {
      setIsSubmittingTestimonial(false);
    }
  };

  const handleEditTestimonial = (item) => {
    setTestimonialForm({
      customerName: item.name || '',
      customerEmail: item.email || '',
      text: item.text || '',
      rating: item.rating || 5
    });
    setEditingTestimonialId(item.id);
  };

  const handleDeleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      await testimonialService.delete(id);
      setTestimonials((prev) => prev.filter((item) => item.id !== id));
      setPendingTestimonials((prev) => prev.filter((item) => item.id !== id));
      toast.success('Testimonial deleted successfully');
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  const resetTestimonialForm = () => {
    setTestimonialForm({
      customerName: '',
      customerEmail: '',
      text: '',
      rating: 5
    });
    setEditingTestimonialId(null);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen pt-24 bg-gradient-to-b from-compassionScale-50 to-white">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <p className="text-compassion-foreground text-lg">Loading admin tools...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen pt-24 bg-gradient-to-b from-compassionScale-50 to-white">
      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="mb-10">
          <h1 className="font-heading font-bold text-4xl text-spiritual">Admin Dashboard</h1>
          <p className="text-compassion-foreground mt-2">
            Manage products, gallery, and testimonials. All changes are synced with the server.
          </p>
        </div>

        {isLoadingBackend && (
          <div className="text-center py-12">
            <Loader className="w-8 h-8 animate-spin text-spiritual mx-auto" />
            <p className="text-compassion-foreground mt-4">Loading data from server...</p>
          </div>
        )}

        {!isLoadingBackend && (
          <>
            {/* Tabs */}
            <div className="flex gap-2 mb-8 border-b border-compassionScale-200">
              {['products', 'gallery', 'testimonials'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => {
                    setActiveTab(tab);
                    if (tab === 'products') setSelectedCategory('');
                  }}
                  className={`px-6 py-3 font-semibold text-sm capitalize transition-colors ${
                    activeTab === tab
                      ? 'text-spiritual border-b-2 border-sacred'
                      : 'text-compassion-foreground hover:text-spiritual'
                  }`}
                >
                  {tab === 'products' && `📦 Products (${backendProducts.length})`}
                  {tab === 'gallery' && `🖼️ Gallery (${backendGalleryItems.length})`}
                  {tab === 'testimonials' && `⭐ Testimonials (${testimonials.length})`}
                </button>
              ))}
            </div>

            {/* Products Tab */}
            {activeTab === 'products' && (
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Product Form */}
                <Card className="lg:col-span-1">
                  <CardContent className="p-6">
                    <h2 className="font-heading font-bold text-xl text-spiritual mb-4">
                      {editingProductId ? '✏️ Edit Product' : '➕ New Product'}
                    </h2>
                    <form onSubmit={handleSaveProduct} className="space-y-4">
                      <Input
                        name="name"
                        placeholder="Product name *"
                        value={productForm.name}
                        onChange={handleProductInputChange}
                      />
                      <div className="grid sm:grid-cols-2 gap-3">
                        <select
                          name="category"
                          value={productForm.category}
                          onChange={handleProductInputChange}
                          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                        >
                          {productCategories.map((category) => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                        <Input
                          name="price"
                          placeholder="Price *"
                          value={productForm.price}
                          onChange={handleProductInputChange}
                        />
                      </div>
                      <Textarea
                        name="description"
                        placeholder="Short description"
                        value={productForm.description}
                        onChange={handleProductInputChange}
                        rows={2}
                      />
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs text-compassion-foreground font-medium">
                          <ImagePlus className="w-4 h-4" />
                          Upload image or paste URL
                        </div>
                        <Input type="file" accept="image/*" onChange={handleProductImageChange} />
                        <Input
                          placeholder="Or paste image URL..."
                          value={productForm.image}
                          onChange={handleProductImageUrl}
                        />
                        {productImagePreview && (
                          <img
                            src={productImagePreview}
                            alt="Product preview"
                            className="w-full h-32 object-cover rounded-lg border border-compassionScale-200"
                          />
                        )}
                      </div>
                      <label className="flex items-center gap-2 text-sm text-compassion-foreground cursor-pointer">
                        <input
                          type="checkbox"
                          name="inStock"
                          checked={productForm.inStock}
                          onChange={handleProductInputChange}
                          className="w-4 h-4"
                        />
                        <span>In stock</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          type="submit" 
                          disabled={isSubmittingProduct}
                          className="flex-1 bg-spiritual hover:bg-spiritual/90 text-white"
                        >
                          {isSubmittingProduct ? '⏳ Saving...' : editingProductId ? '💾 Update' : '➕ Create'}
                        </Button>
                        {editingProductId && (
                          <Button type="button" variant="outline" onClick={resetProductForm} className="text-red-600">
                            <XCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </form>
                  </CardContent>
                </Card>

                {/* Products List */}
                <Card className="lg:col-span-2">
                  <CardContent className="p-6">
                    <div className="mb-6">
                      <div className="flex gap-4 flex-col sm:flex-row">
                        <div className="flex-1 relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-compassionScale-400" />
                          <Input
                            placeholder="Search products..."
                            value={productSearch}
                            onChange={(e) => setProductSearch(e.target.value)}
                            className="pl-10"
                          />
                        </div>
                        <select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="h-10 rounded-md border border-input bg-background px-3 text-sm w-full sm:w-auto"
                        >
                          <option value="">All Categories</option>
                          {productCategories.map((category) => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {filteredProducts.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">
                          {productSearch || selectedCategory ? 'No products match your search' : 'No products yet'}
                        </p>
                      ) : (
                        filteredProducts.map((product) => (
                          <div
                            key={product.id}
                            className="flex items-center gap-4 border border-compassionScale-100 rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
                          >
                            {product.image && (
                              <img src={product.image} alt={product.name} className="w-16 h-16 object-cover rounded-md border" />
                            )}
                            <div className="flex-1">
                              <div className="flex items-start justify-between mb-1">
                                <p className="font-semibold text-spiritual">{product.name}</p>
                                <span className={`text-xs px-2 py-1 rounded ${product.inStock ? 'bg-compassionScale-100 text-spiritual' : 'bg-red-100 text-red-700'}`}>
                                  {product.inStock ? '✓ In Stock' : '✗ Out of Stock'}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600">{product.category} • {product.price}</p>
                              {product.description && (
                                <p className="text-xs text-gray-500 line-clamp-1">{product.description}</p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditProduct(product)}
                              >
                                <PencilLine className="w-4 h-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteProduct(product.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Gallery Tab */}
            {activeTab === 'gallery' && (
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Gallery Form */}
                <Card className="lg:col-span-1">
                  <CardContent className="p-6">
                    <h2 className="font-heading font-bold text-xl text-spiritual mb-4">
                      {editingGalleryId ? '✏️ Edit Item' : '➕ New Item'}
                    </h2>
                    <form onSubmit={handleSaveGallery} className="space-y-4">
                      <Input
                        name="title"
                        placeholder="Title *"
                        value={galleryForm.title}
                        onChange={handleGalleryInputChange}
                      />
                      <div className="grid sm:grid-cols-2 gap-3">
                        <select
                          name="category"
                          value={galleryForm.category}
                          onChange={handleGalleryInputChange}
                          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                        >
                          {galleryCategories.map((category) => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                        <Input
                          type="date"
                          name="date"
                          value={galleryForm.date}
                          onChange={handleGalleryInputChange}
                        />
                      </div>
                      <Textarea
                        name="description"
                        placeholder="Description"
                        value={galleryForm.description}
                        onChange={handleGalleryInputChange}
                        rows={2}
                      />
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs text-compassion-foreground font-medium">
                          <ImagePlus className="w-4 h-4" />
                          Upload image or paste URL
                        </div>
                        <Input type="file" accept="image/*" onChange={handleGalleryImageChange} />
                        <Input
                          placeholder="Or paste image URL..."
                          value={galleryForm.src}
                          onChange={handleGalleryImageUrl}
                        />
                        {galleryImagePreview && (
                          <img
                            src={galleryImagePreview}
                            alt="Gallery preview"
                            className="w-full h-32 object-cover rounded-lg border border-compassionScale-200"
                          />
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          type="submit" 
                          disabled={isSubmittingGallery}
                          className="flex-1 bg-spiritual hover:bg-spiritual/90 text-white"
                        >
                          {isSubmittingGallery ? '⏳ Saving...' : editingGalleryId ? '💾 Update' : '➕ Create'}
                        </Button>
                        {editingGalleryId && (
                          <Button type="button" variant="outline" onClick={resetGalleryForm} className="text-red-600">
                            <XCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </form>
                  </CardContent>
                </Card>

                {/* Gallery List */}
                <Card className="lg:col-span-2">
                  <CardContent className="p-6">
                    <div className="mb-6">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-compassionScale-400" />
                        <Input
                          placeholder="Search gallery items..."
                          value={gallerySearch}
                          onChange={(e) => setGallerySearch(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {filteredGalleryItems.length === 0 ? (
                        <p className="text-center text-gray-500 py-8">
                          {gallerySearch ? 'No gallery items match your search' : 'No gallery items yet'}
                        </p>
                      ) : (
                        filteredGalleryItems.map((item) => (
                          <div
                            key={item.id}
                            className="flex items-center gap-4 border border-compassionScale-100 rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
                          >
                            {item.src && (
                              <img src={item.src} alt={item.title} className="w-16 h-16 object-cover rounded-md border" />
                            )}
                            <div className="flex-1">
                              <div className="flex items-start justify-between mb-1">
                                <p className="font-semibold text-spiritual">{item.title}</p>
                              </div>
                              <p className="text-sm text-gray-600">{item.category} {item.date && `• ${item.date}`}</p>
                              {item.description && (
                                <p className="text-xs text-gray-500 line-clamp-1">{item.description}</p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditGallery(item)}
                              >
                                <PencilLine className="w-4 h-4" />
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteGallery(item.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Testimonials Tab */}
            {activeTab === 'testimonials' && (
              <div className="grid lg:grid-cols-3 gap-8">
                {/* Testimonial Form */}
                <Card className="lg:col-span-1">
                  <CardContent className="p-6">
                    <h2 className="font-heading font-bold text-xl text-spiritual mb-4">
                      {editingTestimonialId ? '✏️ Edit Testimonial' : '➕ New Testimonial'}
                    </h2>
                    <form onSubmit={(e) => { e.preventDefault(); handleSubmitTestimonial(); }} className="space-y-4">
                      <Input
                        placeholder="Customer Name *"
                        value={testimonialForm.customerName}
                        onChange={(e) => setTestimonialForm({ ...testimonialForm, customerName: e.target.value })}
                        required
                      />
                      <Input
                        type="email"
                        placeholder="Customer Email *"
                        value={testimonialForm.customerEmail}
                        onChange={(e) => setTestimonialForm({ ...testimonialForm, customerEmail: e.target.value })}
                        required
                      />
                      <Textarea
                        placeholder="Testimonial text *"
                        value={testimonialForm.text}
                        onChange={(e) => setTestimonialForm({ ...testimonialForm, text: e.target.value })}
                        required
                        rows={3}
                      />
                      <div>
                        <label className="block text-sm font-medium text-compassion-foreground mb-3">Rating</label>
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              type="button"
                              onClick={() => setTestimonialForm({ ...testimonialForm, rating: star })}
                              className="focus:outline-none transition-transform hover:scale-110"
                            >
                              <Star
                                className={`w-6 h-6 ${
                                  star <= testimonialForm.rating
                                    ? 'text-yellow-400 fill-yellow-400'
                                    : 'text-gray-300'
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="submit"
                          disabled={isSubmittingTestimonial}
                          className="flex-1 bg-spiritual hover:bg-spiritual/90 text-white"
                        >
                          {isSubmittingTestimonial ? '⏳ Saving...' : editingTestimonialId ? '💾 Update' : '➕ Create'}
                        </Button>
                        {editingTestimonialId && (
                          <Button
                            type="button"
                            variant="outline"
                            onClick={resetTestimonialForm}
                            className="text-red-600"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </form>
                  </CardContent>
                </Card>

                {/* Testimonials List */}
                <div className="lg:col-span-2 space-y-6">
                  {/* Pending Approvals */}
                  {pendingTestimonials.length > 0 && (
                    <Card>
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-spiritual mb-4 flex items-center gap-2">
                          <span className="inline-block w-2 h-2 bg-yellow-400 rounded-full animate-pulse"></span>
                          Pending Approval ({pendingTestimonials.length})
                        </h3>
                        <div className="space-y-3 max-h-60 overflow-y-auto">
                          {pendingTestimonials.map((item) => (
                            <div
                              key={item.id}
                              className="p-4 bg-yellow-50 rounded-lg border border-yellow-200 hover:shadow-md transition-shadow"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <p className="font-semibold text-spiritual">{item.name}</p>
                                  <p className="text-xs text-yellow-700 mt-1">⏳ Pending customer approval</p>
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={() => handleDeleteTestimonial(item.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                              <p className="text-sm text-gray-700 mb-2">"{item.text}"</p>
                              <div className="flex gap-1">
                                {[...Array(item.rating)].map((_, i) => (
                                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Approved Testimonials */}
                  <Card>
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-spiritual mb-4 flex items-center gap-2">
                        <span className="inline-block w-2 h-2 bg-spiritual rounded-full"></span>
                        Approved Testimonials ({testimonials.length})
                      </h3>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {testimonials.length === 0 ? (
                          <p className="text-center text-gray-500 py-8">No approved testimonials yet</p>
                        ) : (
                          testimonials.map((item) => (
                            <div
                              key={item.id}
                              className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <p className="font-semibold text-spiritual">{item.name}</p>
                                  <p className="text-xs text-compassion-foreground">{item.email}</p>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleEditTestimonial(item)}
                                  >
                                    <PencilLine className="w-4 h-4" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="border-red-200 text-red-600 hover:bg-red-50"
                                    onClick={() => handleDeleteTestimonial(item.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                              <p className="text-sm text-gray-700 mb-2">"{item.text}"</p>
                              <div className="flex gap-1">
                                {[...Array(item.rating)].map((_, i) => (
                                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                                ))}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Admin;
